import { css, Global } from '@emotion/react';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';

import { EmbedVideo } from '@/components/ui/features/embed/EmbedPlayerVideo';
import { accessFrom } from '@/constants/custom-request-headers';
import { AccessFrom } from '@/enums/AccessFrom';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { queryLectureAccessPermission } from '@/queries/lecture-access-permissions';
import { queryUserSession } from '@/queries/sessions';
import { SESSION_ME } from '@/utils/constants';
import { generateStore } from '@/utils/generateStore';
import { Hydrate } from '@/utils/swr/Hydrate';
import { SlugIdParam, urlQueryStringValidate } from '@/utils/url-query-string';

const EmbedPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ fallback }) => {
  return (
    <Hydrate fallback={fallback}>
      <Global
        styles={css`
          .grecaptcha-badge {
            visibility: hidden !important;
          }
        `}
      />
      <EmbedVideo />
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { id } = urlQueryStringValidate<{ id: ReturnType<typeof SlugIdParam> }>(context.query, { id: SlugIdParam });

  // temp: https://app.productive.io/1-infinum/tasks/6934694
  if (!id) {
    const idQuery = context.query?.id?.toString();

    if (idQuery && idQuery.match(/^\d+$/)) {
      const lecture = await store.fetchResource(queryThinLectureWithTagAndEvent(idQuery)).catch(() => null);

      if (lecture) {
        return {
          redirect: {
            destination: `/lecture/${lecture.data.linkSlug}`,
            permanent: true,
          },
        };
      }
    }
  }

  const lecture = await store.fetchResource(queryThinLectureWithTagAndEvent(id)).catch(() => null);

  if (!lecture) {
    return {
      notFound: true,
    };
  }

  const concurrentPromises = [];

  try {
    await store.fetchResource(queryUserSession(SESSION_ME));
  } catch (error) {
    // not logged in
  }

  try {
    await Promise.all([store.fetchResource(queryLectureAccessPermission(id)), ...concurrentPromises]);
  } catch (err) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      id,
      fallback: store.fallback,
      accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
    },
  };
};

export default EmbedPage;
